import React from 'react';
import { imageSrc } from '@/data/previewMeta';
import useIntakeAssets from '@/hooks/useIntakeAssets';
import { assetAlt, assetUrl, illustrationKey } from '@/services/intakeAssets';

// Wraps an unchanged step component with the omzo-style chrome: a two-part
// headline (plain + orange accent) above, then the illustration, then the step
// itself (its own <h2> is the question), then a "← Back" button. The hint is
// rendered under the headline only visually — the step's own copy is still
// what goes to the provider. meta=null → no headline/image, just the Back.
const PreviewStepFrame = ({ meta, onBack, children }) => {
  const { assets } = useIntakeAssets();
  const [plain, accent] = (meta && meta.headline) || [];
  // A published override replaces the bundled SVG; the key is the same
  // '<flow>/<name>' previewMeta already stores, dotted.
  // (docs/INTAKE_PRD_ASSET_MANIFEST_READ_2026_09_27.md §3B)
  const bundled = meta ? imageSrc(meta.image) : null;
  const key = meta ? illustrationKey(meta.image) : null;
  const src = assetUrl(assets, key, bundled);
  // Bundled illustrations are decorative. An override may carry real alt text,
  // and when it does the wrapper must stop being aria-hidden — otherwise the
  // alt is announced to nobody.
  const alt = assetAlt(assets, key, '');
  return (
    <div data-testid="preview-step-frame">
      {(plain || accent) && (
        <h1 className="wl-headline mb-2">
          {plain} {accent ? <em>{accent}</em> : null}
        </h1>
      )}
      {meta && meta.hint && <p className="text-sm mb-5" style={{ color: 'var(--wl-body)' }}>{meta.hint}</p>}
      {src && (
        <div className="wl-hero mb-6" aria-hidden={alt ? undefined : 'true'}>
          <img src={src} alt={alt} loading="lazy" />
        </div>
      )}
      {children}
      {onBack && (
        <button type="button" onClick={onBack} className="wl-back mt-4" data-testid="step-back-button">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      )}
    </div>
  );
};

export default PreviewStepFrame;
